import React, { useState, useEffect } from 'react';
import About from './About';
import WorkerStatus from './workerstatus';
import Contact from './contact';
import Management from './Management';
import TermsConditions from './TermsConditions';
import PrivacyPolicy from './PrivacyPolicy';
import DisclaimerPolicy from './DisclaimerPolicy';
import CookiePolicy from './CookiePolicy';
import Career from './Career';
import Gallery from './Gallery';
import { API_BASE_URL } from './config';


/* ── Google Translate Loader ── */
const loadGoogleTranslate = (lang) => {
  // Remove existing translate element and script
  const existingEl = document.getElementById('google_translate_element');
  if (existingEl) existingEl.innerHTML = '';
  const existingScript = document.getElementById('gt-script');
  if (existingScript) existingScript.remove();

  if (lang === 'en') {
    // Reset to English — clear cookie and reload
    document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/';
    document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=' + window.location.hostname;
    // Force reload to reset
    window.location.reload();
    return;
  }

  const langCode = lang === 'hi' ? 'hi' : 'as';
  window.googleTranslateElementInit = function () {
    new window.google.translate.TranslateElement(
      { pageLanguage: 'en', includedLanguages: 'hi,as', autoDisplay: false },
      'google_translate_element'
    );
    // Auto-select the desired language after a brief delay
    setTimeout(() => {
      const select = document.querySelector('.goog-te-combo');
      if (select) {
        select.value = langCode;
        select.dispatchEvent(new Event('change'));
      }
    }, 1200);
  };

  const script = document.createElement('script');
  script.id = 'gt-script';
  script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
  document.body.appendChild(script);
};

/* ─────────────────────────── SERVICES PAGE ─────────────────────────── */
const ServicesPage = ({ setView, setShowLogin, services = [] }) => {

  return (
    <div className="bg-slate-50 min-h-screen font-sans pb-16">
      {/* Hero */}
      <div className="relative text-white overflow-hidden" style={{ minHeight: '340px' }}>
        <img src="/SG (4).jpg" alt="Our Offerings" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#001529] via-[#0d2d6b]/90 to-[#0d2d6b]/50" />
        <div className="absolute top-0 right-0 opacity-5 text-[180px] font-black select-none leading-none -mr-8 -mt-8">SGCL</div>
        <div className="relative z-10 px-4 md:px-20 py-16 max-w-4xl">
          <span className="bg-govt-orange text-white px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest">Our Offerings</span>
          <h1 className="text-4xl md:text-6xl font-black mt-4 mb-6 leading-tight uppercase italic">
            Our <span className="text-govt-orange">Services</span>
          </h1>
          <p className="text-lg md:text-xl text-blue-100 font-bold border-l-4 border-govt-orange pl-6 italic max-w-3xl leading-relaxed">
            Transforming Indian Railways freight operations through structured workforce management, digital monitoring, and comprehensive worker welfare initiatives.
          </p>
        </div>
        {/* Tricolor border strip */}
        <div className="absolute bottom-0 left-0 right-0 h-1.5 flex">
          <div className="flex-1 bg-[#FF9933]" />
          <div className="flex-1 bg-white" />
          <div className="flex-1 bg-[#138808]" />
        </div>
      </div>

      {/* Services Grid */}
      <div className="container mx-auto px-4 md:px-20 -mt-10 relative z-20">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-16">
          {services.map((s, i) => {
            const colorGrad = s.colorGradient || 'from-blue-700 to-blue-500';
            return (
              <div
                key={i}
                onClick={() => {
                  if (s.link) window.open(s.link, "_blank");
                  else if (s.useModal) setShowLogin(true);
                  else if (s.id) setView(s.id);
                }}
                className="group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 border border-slate-100 overflow-hidden flex flex-col sm:flex-row cursor-pointer"
              >
                {/* Colored Side/Top bar */}
                <div className={`w-full sm:w-2 h-2 sm:h-auto bg-gradient-to-b ${colorGrad} shrink-0`} />

                <div className="p-6 flex flex-col sm:flex-row gap-5 items-start flex-1 min-w-0">
                  {/* Icon wrapper */}
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorGrad} flex items-center justify-center text-2xl shadow-md shrink-0 overflow-hidden group-hover:scale-110 transition-transform duration-300`}>
                    {s.iconType === 'img' ? (
                      <img src={s.icon} alt={s.title} className="w-8 h-8 object-contain" />
                    ) : (
                      s.icon
                    )}
                  </div>

                  {/* Info block */}
                  <div className="flex-1 min-w-0">
                    {s.dept && (
                      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1 leading-tight">
                        {s.dept}
                      </p>
                    )}
                    <h3 className="font-black text-govt-blue text-[14px] uppercase mb-1.5 leading-tight tracking-wide group-hover:text-govt-orange transition-colors">
                      {s.title}
                    </h3>
                    <p className="text-slate-500 text-[12px] leading-relaxed font-semibold mb-4">
                      {s.desc}
                    </p>
                    <div className="text-[10px] font-black uppercase tracking-widest text-govt-orange flex items-center gap-1 group-hover:translate-x-1.5 transition-transform duration-200">
                      {s.cta} <span className="text-xs">→</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA Banner */}
        <div className="bg-govt-blue rounded-3xl p-10 md:p-14 text-center shadow-2xl border-b-8 border-govt-orange">
          <h2 className="text-2xl md:text-4xl font-black text-white uppercase italic mb-4">Ready to Partner with SGCL?</h2>
          <p className="text-blue-200 font-semibold mb-8 max-w-xl mx-auto">
            For service enquiries, worker registration, or stakeholder coordination — reach out to our official helpline or write to us.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button onClick={() => setView('contact')} className="bg-govt-orange text-white px-10 py-3.5 rounded font-black uppercase text-xs shadow-[0_5px_0_rgb(194,65,12)] active:translate-y-1 transition-all">Contact Us</button>
            <button onClick={() => setShowLogin(true)} className="bg-white text-govt-blue px-10 py-3.5 rounded font-black uppercase text-xs hover:bg-slate-100 transition-colors">Authority Login</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────── NOTICES PAGE ─────────────────────────── */
const NoticesPage = ({ setView, notices = [] }) => {
  const [activeFilter, setActiveFilter] = React.useState('All');

  const categories = ['All', ...new Set(notices.map(n => n.category))];
  const filtered = activeFilter === 'All' ? notices : notices.filter(n => n.category === activeFilter);

  return (
    <div className="font-sans pb-16 relative overflow-hidden animate-in fade-in duration-500" style={{ background: 'linear-gradient(160deg, #f0f4ff 0%, #f8fafc 50%, #fff7ed 100%)' }}>
      {/* Large background watermark */}
      <div className="absolute top-[45%] left-[50%] -translate-x-[50%] -translate-y-[50%] w-[550px] h-[550px] opacity-[0.025] pointer-events-none z-0 text-govt-blue select-none">
        <svg className="w-full h-full animate-[spin_120s_linear_infinite]" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="50" cy="50" r="45" stroke="currentColor" strokeWidth="2.5" />
          <circle cx="50" cy="50" r="8" fill="currentColor" />
          {Array.from({ length: 24 }).map((_, i) => {
            const angle = (i * 360) / 24;
            return (
              <line
                key={i}
                x1="50"
                y1="50"
                x2={50 + 37 * Math.cos((angle * Math.PI) / 180)}
                y2={50 + 37 * Math.sin((angle * Math.PI) / 180)}
                stroke="currentColor"
                strokeWidth="1.2"
              />
            );
          })}
        </svg>
      </div>
      {/* Hero */}
      <div className="relative text-white overflow-hidden" style={{ minHeight: '340px' }}>
        <img src="/SG (5).jpg" alt="Notices" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#001529] via-[#0d2d6b]/90 to-[#0d2d6b]/50" />
        <div className="absolute top-0 right-0 opacity-5 text-[180px] font-black select-none leading-none -mr-8 -mt-8">SGCL</div>
        <div className="relative z-10 px-4 md:px-20 py-16 max-w-4xl">
          <span className="bg-govt-orange text-white px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest">Official Communications</span>
          <h1 className="text-4xl md:text-6xl font-black mt-4 mb-5 leading-tight uppercase italic">
            Notices &amp; <span className="text-govt-orange">Updates</span>
          </h1>
          <p className="text-blue-100 font-bold border-l-4 border-govt-orange pl-5 italic max-w-2xl leading-relaxed text-base md:text-lg mb-8">
            Official circulars, operational updates, welfare announcements, and compliance notices from SGCL.
          </p>
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2 bg-green-500/20 border border-green-400/30 rounded-full px-4 py-2">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-green-300 text-[10px] font-black uppercase tracking-widest">{notices.length} Active Notices</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2">
              <span className="text-white/70 text-[10px] font-black uppercase tracking-widest">Last Updated: Jun 2026</span>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1.5 flex">
          <div className="flex-1 bg-[#FF9933]" /><div className="flex-1 bg-white" /><div className="flex-1 bg-[#138808]" />
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-20 py-10">
        {/* Category Filter Pills */}
        <div className="flex flex-wrap gap-3 mb-8">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveFilter(cat)}
              className={`px-5 py-2 rounded-full text-xs font-black uppercase tracking-widest border transition-all duration-200
                ${activeFilter === cat
                  ? 'bg-govt-blue text-white border-govt-blue shadow-md'
                  : 'bg-white text-slate-400 border-slate-200 hover:border-govt-blue hover:text-govt-blue'
                }`}
            >
              {cat}
              {cat === 'All' ? ` (${notices.length})` : ` (${notices.filter(n => n.category === cat).length})`}
            </button>
          ))}
        </div>

        {/* Notice Cards Grid */}
        <div className="space-y-4">
          {filtered.map((n, idx) => (
            <div
              key={n.id}
              className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border-l-4 flex flex-col md:flex-row"
              style={{ borderLeftColor: n.tagColor }}
            >
              {/* Left accent — date + tag */}
              <div
                className="md:w-48 shrink-0 flex md:flex-col items-center md:items-start justify-between md:justify-start gap-3 p-4 md:p-6"
                style={{ backgroundColor: n.accentColor }}
              >
                <div className="flex items-center gap-2 md:flex-col md:items-start md:gap-1">
                  <span
                    className="text-[9px] font-black uppercase tracking-widest text-white px-2.5 py-1 rounded-full"
                    style={{ backgroundColor: n.tagColor }}
                  >{n.tag}</span>
                  <span className="text-[10px] font-black uppercase text-slate-500">{n.date}</span>
                </div>
                <div
                  className="hidden md:flex w-12 h-12 rounded-xl items-center justify-center text-2xl mt-4 shadow-sm"
                  style={{ backgroundColor: n.borderColor }}
                >{n.icon}</div>
              </div>

              {/* Content */}
              <div className="flex-1 p-5 md:p-7 flex flex-col justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span
                      className="text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full border"
                      style={{ color: n.tagColor, backgroundColor: n.accentColor, borderColor: n.borderColor }}
                    >{n.category}</span>
                    <span className="text-slate-300 text-[10px] font-mono">{n.id}</span>
                  </div>
                  <h3 className="font-black text-slate-900 text-base md:text-lg leading-tight mb-3 group-hover:text-govt-blue transition-colors">{n.title}</h3>
                  <p className="text-slate-500 text-[13px] leading-relaxed font-medium">{n.desc}</p>
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                  <span className="text-[9px] font-black uppercase tracking-widest text-slate-300">Official Notice</span>
                  <div
                    onClick={() => {
                      if (n.fileUrl) {
                        window.open(`${API_BASE_URL}${n.fileUrl}`, '_blank');
                      }
                    }}
                    className={`flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest px-3 py-2 rounded-xl text-white group-hover:translate-x-1 transition-transform ${n.fileUrl ? 'cursor-pointer hover:brightness-110 active:scale-95' : 'opacity-50'}`}
                    style={{ backgroundColor: n.tagColor }}
                  >{n.fileUrl ? 'View Details →' : 'Official Notice'}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Archive Note */}
        <div className="mt-12 bg-govt-blue rounded-2xl p-8 text-center shadow-xl border-b-4 border-govt-orange">
          <p className="text-[10px] font-black uppercase tracking-widest text-blue-300 mb-2">Notice Archive</p>
          <p className="text-white font-bold text-sm">
            For notices prior to April 2026, contact the Registered Office or email{' '}
            <a href="mailto:support@shramjivicorp.com" className="text-govt-orange underline hover:text-white transition-colors">
              support@shramjivicorp.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────── DOWNLOADS PAGE ─────────────────────────── */
const DownloadsPage = ({ downloads = [] }) => {
  const [expandedItem, setExpandedItem] = useState(null);

  const groupedDownloads = [
    {
      category: 'Registration Forms', icon: '📋', color: '#0d2d6b', items: downloads.filter(d => d.category === 'Registration Forms')
    },
    {
      category: 'Welfare Schemes', icon: '🛡️', color: '#064e3b', items: downloads.filter(d => d.category === 'Welfare Schemes')
    },
    {
      category: 'Circulars & Orders', icon: '📜', color: '#92400e', items: downloads.filter(d => d.category === 'Circulars & Orders')
    },
  ];

  return (
    <div className="font-sans pb-20" style={{ background: 'linear-gradient(160deg, #f0f4ff 0%, #f8fafc 60%, #fff7ed 100%)' }}>
      {/* Hero */}
      <div className="relative text-white overflow-hidden" style={{ minHeight: '300px' }}>
        <img src="/g3.png" alt="Downloads" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#001529] via-[#0d2d6b]/90 to-[#0d2d6b]/60" />
        <div className="absolute top-0 right-0 opacity-5 text-[180px] font-black select-none leading-none -mr-8 -mt-8">SGCL</div>
        <div className="relative z-10 px-4 md:px-20 py-16 max-w-4xl">
          <span className="bg-govt-orange text-white px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest">Document Repository</span>
          <h1 className="text-4xl md:text-6xl font-black mt-4 mb-5 leading-tight uppercase italic">
            Downloads &amp; <span className="text-govt-orange">Resources</span>
          </h1>
          <p className="text-blue-100 font-bold border-l-4 border-govt-orange pl-5 italic max-w-2xl leading-relaxed text-base">
            Official forms, certificates, compliance documents, welfare guides, and circulars for workers, partners, and authorities.
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1.5 flex">
          <div className="flex-1 bg-[#FF9933]" /><div className="flex-1 bg-white" /><div className="flex-1 bg-[#138808]" />
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-20 py-12">
        <div className="grid gap-8">
          {groupedDownloads.map((cat, ci) => (
            <div key={ci} className="bg-white rounded-3xl shadow-md border border-slate-100 overflow-hidden">
              {/* Category Header */}
              <div className="flex items-center gap-4 px-7 py-5 border-b border-slate-100" style={{ background: `${cat.color}08` }}>
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shadow"
                  style={{ backgroundColor: cat.color }}
                >{cat.icon}</div>
                <div>
                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Document Category</p>
                  <h3 className="font-black uppercase text-base" style={{ color: cat.color }}>{cat.category}</h3>
                </div>
                <span className="ml-auto text-[9px] font-black uppercase tracking-widest text-slate-300">{cat.items?.length || 0} files</span>
              </div>
              {/* File Items */}
              <div className="divide-y divide-slate-50">
                {cat.items?.map((file, fi) => {
                  const isExpanded = expandedItem === file.name;
                  return (
                    <div key={fi} className="group flex flex-col border-b border-slate-50 last:border-b-0">
                      {/* Main Item Row */}
                      <div
                        onClick={() => {
                          if (file.isDropdown) {
                            setExpandedItem(isExpanded ? null : file.name);
                          } else if (file.fileUrl) {
                            window.open(`${API_BASE_URL}${file.fileUrl}`, '_blank');
                          }
                        }}
                        className="flex items-center gap-4 px-7 py-4 hover:bg-slate-50 transition-colors cursor-pointer"
                      >
                        <div className="w-10 h-10 rounded-xl bg-slate-100 group-hover:bg-govt-orange/10 flex items-center justify-center text-lg shrink-0 transition-colors">
                          {file.isDropdown ? '📂' : '📄'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-black text-slate-800 text-sm group-hover:text-govt-blue transition-colors truncate">{file.name}</p>
                          <p className="text-[10px] text-slate-400 font-bold mt-0.5 uppercase tracking-wider">{file.size} &bull; {file.date}</p>
                        </div>
                        <span
                          className="text-[8px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full text-white shrink-0"
                          style={{ backgroundColor: cat.color }}
                        >{file.tag}</span>
                        <div className="w-8 h-8 rounded-full bg-slate-100 group-hover:bg-govt-blue flex items-center justify-center transition-colors shrink-0">
                          <span className={`text-slate-400 group-hover:text-white text-sm transition-all duration-300 transform ${file.isDropdown && isExpanded ? 'rotate-180' : ''}`}>
                            {file.isDropdown ? '▼' : '↓'}
                          </span>
                        </div>
                      </div>

                      {/* Dropdown Links Panel */}
                      {file.isDropdown && isExpanded && (
                        <div className="bg-slate-50/70 border-t border-slate-100 px-8 py-3 space-y-2">
                          {file.links.map((link, li) => (
                            <a
                              key={li}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center justify-between p-3 rounded-xl border border-slate-100 bg-white hover:border-govt-orange hover:shadow-sm transition-all"
                            >
                              <div className="flex items-center gap-3">
                                <span className="text-govt-orange">📄</span>
                                <span className="text-xs font-bold text-slate-700">{link.name}</span>
                              </div>
                              <span className="text-slate-400 text-xs font-black uppercase tracking-widest flex items-center gap-1 hover:text-govt-blue">
                                Download <span className="text-sm">↓</span>
                              </span>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Note */}
        <div className="mt-10 bg-govt-blue rounded-2xl p-7 text-center shadow-xl border-b-4 border-govt-orange">
          <p className="text-white font-bold text-sm">
            Documents are for official reference. For physical copies or certified duplicates, contact{' '}
            <a href="mailto:support@shramjivicorp.com" className="text-govt-orange underline hover:text-white transition-colors">
              support@shramjivicorp.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────── WE DON'T SELL DATA PAGE ─────────────────────────── */
const NoSellDataPage = () => {
  return (
    <div className="font-sans pb-20" style={{ background: 'linear-gradient(160deg, #f0f4ff 0%, #f8fafc 60%, #fff7ed 100%)' }}>
      {/* Hero */}
      <div className="relative text-white overflow-hidden" style={{ minHeight: '300px' }}>
        <img src="/SG (2).jpg" alt="Privacy Commitment" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#001529] via-[#0d2d6b]/90 to-[#0d2d6b]/60" />
        <div className="absolute top-0 right-0 opacity-5 text-[180px] font-black select-none leading-none -mr-8 -mt-8">SGCL</div>
        <div className="relative z-10 px-4 md:px-20 py-16 max-w-4xl">
          <span className="bg-govt-orange text-white px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest">Privacy Commitment</span>
          <h1 className="text-4xl md:text-5xl font-black mt-4 mb-5 leading-tight uppercase italic">
            We Don't Sell <span className="text-govt-orange">Your Data</span>
          </h1>
          <p className="text-blue-100 font-bold border-l-4 border-govt-orange pl-5 italic max-w-2xl leading-relaxed text-base">
            Our Commitment to Privacy, Trust &amp; Transparency. At Shramjivi Goodshed Corporation Limited (SGCL), we believe that personal information belongs to the individual who provides it.
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1.5 flex">
          <div className="flex-1 bg-[#FF9933]" /><div className="flex-1 bg-white" /><div className="flex-1 bg-[#138808]" />
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-20 py-12 max-w-5xl">
        <div className="bg-white rounded-[2rem] shadow-xl border border-slate-100 p-8 md:p-12 space-y-10">

          {/* Section 1: Our Promise */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-2xl">🤝</span>
              <h3 className="text-xl font-black uppercase text-govt-blue tracking-wide">Our Promise</h3>
            </div>
            <p className="text-slate-600 font-semibold leading-relaxed mb-6 text-sm">
              We do not sell, trade, or monetize your personal data. We are committed to protecting your privacy and maintaining the highest standards of transparency in how information is collected, used, and safeguarded.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50/50 p-6 rounded-2xl border border-slate-100">
              <ul className="space-y-3.5">
                <li className="flex gap-3 items-start text-xs font-bold text-slate-700">
                  <span className="text-red-500 shrink-0">❌</span>
                  <span>We do NOT sell personal information to advertisers.</span>
                </li>
                <li className="flex gap-3 items-start text-xs font-bold text-slate-700">
                  <span className="text-red-500 shrink-0">❌</span>
                  <span>We do NOT sell personal information to marketing companies.</span>
                </li>
                <li className="flex gap-3 items-start text-xs font-bold text-slate-700">
                  <span className="text-red-500 shrink-0">❌</span>
                  <span>We do NOT sell personal information to data brokers.</span>
                </li>
              </ul>
              <ul className="space-y-3.5">
                <li className="flex gap-3 items-start text-xs font-bold text-slate-700">
                  <span className="text-red-500 shrink-0">❌</span>
                  <span>We do NOT sell personal information to third-party businesses.</span>
                </li>
                <li className="flex gap-3 items-start text-xs font-bold text-slate-700">
                  <span className="text-red-500 shrink-0">❌</span>
                  <span>We do NOT trade personal information for commercial gain.</span>
                </li>
                <li className="flex gap-3 items-start text-xs font-bold text-slate-700">
                  <span className="text-red-500 shrink-0">❌</span>
                  <span>We do NOT share personal information for targeted advertising purposes.</span>
                </li>
              </ul>
            </div>
            <div className="mt-6 border-t border-slate-100 pt-6">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Whether you are:</p>
              <div className="flex flex-wrap gap-2">
                {['Website Visitor', 'Job Applicant', 'Registered Worker', 'Contractor', 'Vendor', 'Business Partner', 'Customer'].map((role, idx) => (
                  <span key={idx} className="bg-govt-blue/5 text-govt-blue border border-govt-blue/10 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-wider">
                    {role}
                  </span>
                ))}
              </div>
              <p className="text-xs font-bold text-slate-600 mt-4 leading-relaxed italic">
                Your personal information is handled with extreme dignity and is not treated as a product for sale.
              </p>
            </div>
          </div>

          <hr className="border-slate-100" />

          {/* Section 2: Information We May Collect & Why */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xl">🗂️</span>
                <h4 className="text-sm font-black uppercase text-govt-blue tracking-wider">Information We May Collect</h4>
              </div>
              <p className="text-slate-500 font-semibold text-xs leading-relaxed mb-4">
                Depending on your interaction with our website or services, we may collect:
              </p>
              <ul className="grid grid-cols-2 gap-2 text-xs font-bold text-slate-700">
                {['Name', 'Mobile Number', 'Email Address', 'Address', 'Employment Information', 'Identity Verification Documents', 'Company Information', 'Communication Records', 'Website Usage Information'].map((item, idx) => (
                  <li key={idx} className="flex gap-2 items-center">
                    <span className="text-govt-orange text-xs">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[11px] font-bold text-slate-400 mt-4 leading-relaxed uppercase">
                Such information is collected only for legitimate operational, administrative, security, or compliance purposes.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xl">⚙️</span>
                <h4 className="text-sm font-black uppercase text-govt-blue tracking-wider">Why We Collect Information</h4>
              </div>
              <p className="text-slate-500 font-semibold text-xs leading-relaxed mb-4">
                Information may be used solely for purposes such as:
              </p>
              <ul className="space-y-2 text-xs font-bold text-slate-700">
                {[
                  'Responding to enquiries',
                  'Worker registration and verification',
                  'Employment applications',
                  'Vendor and contractor onboarding',
                  'Service delivery & customer support',
                  'Legal and regulatory compliance',
                  'Website security & internal administration'
                ].map((item, idx) => (
                  <li key={idx} className="flex gap-2 items-center">
                    <span className="text-[#138808] text-xs">✔</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[11px] font-bold text-slate-400 mt-4 leading-relaxed uppercase">
                We collect only the information reasonably necessary for these purposes.
              </p>
            </div>
          </div>

          <hr className="border-slate-100" />

          {/* Section 3: Sharing, Protection & Security */}
          <div className="space-y-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xl">⚖️</span>
                <h4 className="text-sm font-black uppercase text-govt-blue tracking-wider">When Information May Be Shared</h4>
              </div>
              <p className="text-slate-600 font-semibold leading-relaxed mb-4 text-xs">
                Although we do not sell data, information may be disclosed in limited circumstances where required by law or legitimate business necessity. Examples include:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
                <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100 space-y-2.5">
                  <h5 className="font-black text-xs uppercase text-govt-orange">Legal Compliance</h5>
                  <p className="text-[11px] text-slate-500 font-bold leading-relaxed">
                    When required by courts of law, government authorities, regulatory agencies, or law enforcement agencies.
                  </p>
                </div>
                <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100 space-y-2.5">
                  <h5 className="font-black text-xs uppercase text-govt-orange">Service Providers</h5>
                  <p className="text-[11px] text-slate-500 font-bold leading-relaxed">
                    Authorized service providers assisting with hosting, cloud systems, security, or maintenance (strictly bound by confidentiality rules).
                  </p>
                </div>
                <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100 space-y-2.5">
                  <h5 className="font-black text-xs uppercase text-govt-orange">Security & Protection</h5>
                  <p className="text-[11px] text-slate-500 font-bold leading-relaxed">
                    Where necessary to prevent fraud, investigate system misuse, protect public safety, or protect Company assets.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">
              <div className="bg-slate-900 text-white p-6 rounded-2xl border-l-4 border-govt-orange shadow-lg">
                <h4 className="text-xs font-black uppercase text-govt-orange mb-2">No Advertising Data Sales</h4>
                <p className="text-[11px] text-slate-300 font-bold leading-relaxed">
                  We do not operate a business model based on selling user information. We do not create advertising profiles for sale, monetize user identities, or share information with advertising networks for commercial gain.
                </p>
              </div>

              <div className="bg-govt-blue text-white p-6 rounded-2xl border-l-4 border-[#138808] shadow-lg">
                <h4 className="text-xs font-black uppercase text-green-400 mb-2">Worker &amp; Applicant Protection</h4>
                <p className="text-[11px] text-slate-300 font-bold leading-relaxed">
                  Information submitted by workers, applicants, contractors, or partners is handled with strict confidentiality. It is used only for registration, verification, employment-related processes, welfare administration, compliance requirements, or operational management.
                </p>
              </div>
            </div>

            <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xl">🛡️</span>
                <h4 className="text-xs font-black uppercase text-govt-blue tracking-wider">Data Security Measures</h4>
              </div>
              <p className="text-slate-600 font-semibold text-xs leading-relaxed">
                To protect personal information, we implement reasonable administrative and technical security measures, including access controls, password protection, secure hosting environments, data encryption where applicable, and active security monitoring.
              </p>
            </div>
          </div>

          <hr className="border-slate-100" />

          {/* Section 4: Transparency & Contact */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xl">🔍</span>
                <h4 className="text-sm font-black uppercase text-govt-blue tracking-wider">Transparency Matters</h4>
              </div>
              <p className="text-slate-600 font-semibold leading-relaxed mb-4 text-xs">
                Privacy is not merely a legal obligation — it is a corporate responsibility. We believe users should know what information is collected, why it is collected, how it is used, who it is shared with, and that it is not sold for profit.
              </p>
              <div className="bg-govt-orange/10 border border-govt-orange/20 rounded-xl p-5 text-govt-orange font-black text-center text-xs uppercase tracking-widest leading-relaxed">
                "We Don't Sell Your Data. We Protect It."
              </div>
            </div>

            <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100 space-y-4">
              <h4 className="text-xs font-black uppercase text-govt-blue border-b border-slate-200 pb-2 tracking-widest">Contact Information</h4>
              <p className="text-[11px] text-slate-500 font-bold leading-relaxed">
                If you have questions regarding data privacy or information handling practices, please contact our support office:
              </p>
              <div className="text-[11px] font-bold text-slate-700 space-y-1.5 uppercase">
                <p>🏢 Shramjivi Goodshed Corporation Limited</p>
                <p>🌐 Website: www.shramjivicorp.com</p>
                <p>✉️ Email: support@shramjivicorp.com</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

const DEFAULT_NOTICES = [
  { id: 'NOT-2026-007', date: '06 Jun 2026', category: 'Registration', tag: 'NEW', tagColor: '#16a34a', accentColor: '#dcfce7', borderColor: '#86efac', title: 'SGCL Worker Registration Drive — Phase I Open', desc: 'Applications are now open for goodshed workers at NJP, Siliguri, and New Delhi goods sheds. Eligible workers must carry Aadhaar, bank passbook, and past employment proof.', icon: '📋', fileUrl: '' },
  { id: 'NOT-2026-006', date: '04 Jun 2026', category: 'Compliance', tag: 'IMPORTANT', tagColor: '#dc2626', accentColor: '#fee2e2', borderColor: '#fca5a5', title: 'MCA Compliance Deadline — Annual Filing Reminder', desc: 'All directors and officers are reminded of the upcoming MCA annual filing deadline. Documents must be submitted to the compliance team by 15 June 2026.', icon: '⚖️', fileUrl: '' },
  { id: 'NOT-2026-005', date: '01 Jun 2026', category: 'Welfare', tag: 'INFO', tagColor: '#2563eb', accentColor: '#dbeafe', borderColor: '#93c5fd', title: 'ESIC & EPFO Enrolment Camp — Siliguri Office', desc: 'A special ESIC and EPFO enrolment camp will be held at the Siliguri registered office on 10 Jun 2026. All enrolled workers are requested to attend with required documents.', icon: '🏥', fileUrl: '' },
  { id: 'NOT-2026-004', date: '28 May 2026', category: 'Operations', tag: 'UPDATE', tagColor: '#ea580c', accentColor: '#ffedd5', borderColor: '#fdba74', title: 'Digital Attendance System — Pilot Launch at NJP Goodshed', desc: 'The QR-based digital attendance and monitoring system has been successfully piloted at the NJP railway goods shed. Rollout to additional locations is scheduled for July 2026.', icon: '📲', fileUrl: '' },
  { id: 'NOT-2026-003', date: '20 May 2026', category: 'Training', tag: 'INFO', tagColor: '#2563eb', accentColor: '#dbeafe', borderColor: '#93c5fd', title: 'Safety & Skill Development Training Programme — New Delhi', desc: 'A 3-day safety and skill development training programme for goodshed workers was conducted at the New Delhi Head Office. 42 workers were certified in freight handling safety protocols.', icon: '🎓', fileUrl: '' },
  { id: 'NOT-2026-002', date: '10 May 2026', category: 'Corporate', tag: 'CIRCULAR', tagColor: '#7c3aed', accentColor: '#ede9fe', borderColor: '#c4b5fd', title: 'Board Resolution — Appointment of Compliance Officer', desc: 'Pursuant to the Board Meeting held on 08 May 2026, a resolution has been passed for the appointment of a dedicated Compliance Officer for MCA & Labour Law adherence.', icon: '🏛️', fileUrl: '' },
  { id: 'NOT-2026-001', date: '15 Apr 2026', category: 'Incorporation', tag: 'MILESTONE', tagColor: '#0d2d6b', accentColor: '#e0e7ff', borderColor: '#a5b4fc', title: 'SGCL Successfully Incorporated under MCA — CIN Issued', desc: 'Shramjivi Goodshed Corporation Limited has been officially incorporated under the Ministry of Corporate Affairs (MCA), Government of India. CIN: U78300WR2026PLC293826.', icon: '🎉', fileUrl: '' }
];

const DEFAULT_DOWNLOADS = [
  { id: 'dl-1', category: 'Registration Forms', name: 'Nominee Declaration Form', size: 'PDF • 89 KB', date: 'Jun 2026', tag: 'ACTIVE', fileUrl: '', isDropdown: false, links: [] },
  { id: 'dl-2', category: 'Registration Forms', name: 'Worker Update / Correction Form', size: 'PDF • 102 KB', date: 'Jun 2026', tag: 'ACTIVE', fileUrl: '', isDropdown: false, links: [] },
  {
    id: 'dl-3', category: 'Welfare Schemes', name: 'EPFO Downloads', size: '4 Documents', date: 'May 2026', tag: 'ACTS & SCHEMES', fileUrl: '', isDropdown: true, links: [
      { name: "Employees' Provident Funds and Miscellaneous Provisions Act, 1952", url: 'https://www.epfindia.gov.in/site_docs/PDFs/Downloads_PDFs/EPFAct1952.pdf' },
      { name: "Employees' Provident Funds Scheme, 1952", url: 'https://www.epfindia.gov.in/site_docs/PDFs/Downloads_PDFs/EPFScheme.pdf' },
      { name: "Employees' Deposit Linked Insurance Scheme, 1976", url: 'https://www.epfindia.gov.in/site_docs/PDFs/Downloads_PDFs/EDLI_1976.pdf' },
      { name: "Employees' Pension Scheme, 1995 (replacing the Employees' Family Pension Scheme, 1971)", url: 'https://www.epfindia.gov.in/site_docs/PDFs/Downloads_PDFs/EPS95_update102008.pdf' }
    ]
  },
  { id: 'dl-4', category: 'Welfare Schemes', name: 'ESIC Benefits Booklet (Hindi)', size: 'PDF • 1.2 MB', date: 'May 2026', tag: 'NEW', fileUrl: '', isDropdown: false, links: [] },
  { id: 'dl-5', category: 'Welfare Schemes', name: 'e-Shram Card Application Guide', size: 'PDF • 540 KB', date: 'May 2026', tag: 'GUIDE', fileUrl: '', isDropdown: false, links: [] },
  { id: 'dl-6', category: 'Circulars & Orders', name: 'Safety Training Certification Notice', size: 'PDF • 156 KB', date: 'May 2026', tag: 'NOTICE', fileUrl: '', isDropdown: false, links: [] }
];

const DEFAULT_SERVICES = [
  { id: 'worker-status', icon: '📋', iconType: 'emoji', title: 'Worker Status Check', dept: 'SGCL', desc: "Verify your registration status, check scheme eligibility, and track your worker profile with SGCL's official portal.", color: '#0d2d6b', colorGradient: 'from-blue-700 to-blue-500', cta: 'Check Status', link: '', useModal: false },
  { id: 'eshram-card', icon: 'https://eshramwebportal-megh.azureedge.net/nduwwsmedia/themes/images/e-shram-logo-english.png', iconType: 'img', title: 'e-Shram Card', dept: 'Ministry of Labour & Employment', desc: 'Register on the National Database of Unorganised Workers (NDUW). Download your e-Shram card for social security benefits.', color: '#1a6e34', colorGradient: 'from-green-700 to-green-500', cta: 'Visit eshram.gov.in', link: 'https://eshram.gov.in/', useModal: false },
  { id: 'epfo-services', icon: '/epfo.png', iconType: 'img', title: 'EPFO Member Services', dept: "Employees' Provident Fund Organisation", desc: 'Access your PF balance, file withdrawal claims, transfer accounts, and avail all EPFO member services online.', color: '#7b1a1a', colorGradient: 'from-red-700 to-red-500', cta: 'Visit epfindia.gov.in', link: 'https://www.epfindia.gov.in/site_en/For_Employees.php', useModal: false },
  { id: 'esic-services', icon: 'https://upload.wikimedia.org/wikipedia/en/7/77/Employee_State_Insurance_Corporation_Logo.png', iconType: 'img', title: 'ESIC Health Services', dept: "Employees' State Insurance Corporation", desc: 'Avail cashless medical treatment, check IP details, and access all employee state insurance services and benefits.', color: '#3b1078', colorGradient: 'from-purple-700 to-purple-500', link: 'https://www.esic.gov.in/', cta: 'Visit esic.gov.in', useModal: false },
  { id: 'consigner-registration', icon: '🚚', iconType: 'emoji', title: 'Consigner / Consignee Registration', dept: 'SGCL — Freight Services', desc: 'Register as a consigner or consignee for railway goods shed freight operations. Get a verified SGCL freight partner ID for priority handling.', color: '#0e5c5c', colorGradient: 'from-teal-700 to-teal-500', cta: 'Register Now', link: '', useModal: true },
  { id: 'warehouse-services', icon: '🏭', iconType: 'emoji', title: 'Warehouse & Storage Services', dept: 'SGCL — Logistics Services', desc: 'Access SGCL-managed warehouse and storage facilities at railway goods sheds for safe, organised, and monitored goods storage.', color: '#1a4a7a', colorGradient: 'from-cyan-700 to-cyan-500', cta: 'Enquire Now', link: '', useModal: true },
  { id: 'transportation-services', icon: '🛻', iconType: 'emoji', title: 'Transportation Services', dept: 'SGCL — Logistics Services', desc: 'End-to-end transportation coordination for railway freight — last-mile delivery, inter-shed transfer, and fleet management support.', color: '#5c3a00', colorGradient: 'from-amber-600 to-amber-400', cta: 'Enquire Now', link: '', useModal: true },
  { id: 'freight-management', icon: '📦', iconType: 'emoji', title: 'Freight Management', dept: 'SGCL — Freight Operations', desc: 'Comprehensive freight management — booking, documentation, tracking, loading supervision, and delivery confirmation for railway goods.', color: '#1a5c3a', colorGradient: 'from-orange-600 to-orange-400', cta: 'Enquire Now', link: '', useModal: true },
  { id: 'contact', icon: '📬', iconType: 'emoji', title: 'Grievance Desk', dept: 'Complain Desk', desc: 'Register complaints, submit grievances, track resolution status, and seek support for operational or welfare issues.', color: '#9f1239', colorGradient: 'from-rose-700 to-rose-500', cta: 'Contact Desk', link: '', useModal: false }
];

const App = () => {
  const [showLogin, setShowLogin] = useState(false);
  const [view, setView] = useState('home'); // Page routing handle karne ke liye
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeLang, setActiveLang] = useState('en');
  const [showCookieNotice, setShowCookieNotice] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [notices, setNotices] = useState(DEFAULT_NOTICES);
  const [downloads, setDownloads] = useState(DEFAULT_DOWNLOADS);
  const [services, setServices] = useState(DEFAULT_SERVICES);
  const [tickerUpdates, setTickerUpdates] = useState([]);

  const refreshNotices = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/notices`);
      if (res.ok) {
        const data = await res.json();
        setNotices(data);
      }
    } catch (err) {
      console.error('Backend fetch notices failed:', err);
    }
  };

  const refreshDownloads = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/downloads`);
      if (res.ok) {
        const data = await res.json();
        setDownloads(data);
      }
    } catch (err) {
      console.error('Backend fetch downloads failed:', err);
    }
  };

  const refreshServices = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/services`);
      if (res.ok) {
        const data = await res.json();
        setServices(data);
      }
    } catch (err) {
      console.error('Backend fetch services failed:', err);
    }
  };

  const refreshUpdates = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/updates`);
      if (res.ok) {
        const data = await res.json();
        setTickerUpdates(data);
      }
    } catch (err) {
      console.error('Backend fetch updates failed:', err);
    }
  };

  useEffect(() => {
    refreshNotices();
    refreshDownloads();
    refreshServices();
    refreshUpdates();
  }, []);

  const handleLangChange = (lang) => {
    setActiveLang(lang);
    loadGoogleTranslate(lang);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % 3);
    }, 5000); // changes every 5 seconds

    // Check cookie consent
    const consent = localStorage.getItem('sgcl-cookie-consent');
    if (!consent) {
      setShowCookieNotice(true);
    }

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [view]);

  const handleAcceptCookies = () => {
    localStorage.setItem('sgcl-cookie-consent', 'accepted');
    setShowCookieNotice(false);
  };

  const handleDeclineCookies = () => {
    localStorage.setItem('sgcl-cookie-consent', 'declined');
    setShowCookieNotice(false);
  };

  const navLinks = [
    { name: "Home", id: 'home' },
    { name: "About Us", id: 'about' },
    { name: "Management", id: 'management' },
    { name: "Services", id: 'services' },
    { name: "Notices", id: 'notices' },
    { name: "Downloads", id: 'downloads' },
    { name: "Career", id: 'career' },
    { name: "Gallery", id: 'gallery' },
    { name: "Contact", id: 'contact' }
  ];

  return (
    <div className="min-h-screen bg-slate-50 font-sans selection:bg-orange-200">

      {/* Top Tricolor Strip */}
      <div className="h-1 flex">
        <div className="flex-1 bg-[#FF9933]" />
        <div className="flex-1 bg-white" />
        <div className="flex-1 bg-[#138808]" />
      </div>

      {/* Hidden Google Translate element */}
      <div id="google_translate_element" style={{ display: 'none' }} />

      {/* 1. TOP MINISTRY BAR */}
      <div className="bg-govt-blue text-white py-1.5 px-4 md:px-20 flex justify-between text-[10px] md:text-xs font-bold uppercase tracking-wider">
        <span className="truncate mr-2 italic">
          Registered under Ministry of Corporate Affairs (MCA), Govt. of India | CIN: U78300WR2026PLC293826
        </span>
        <div className="flex gap-1 shrink-0 items-center">
          {[{ code: 'en', label: 'English' }, { code: 'hi', label: 'हिन्दी' }, { code: 'as', label: 'অসমীয়া' }].map((lang, i, arr) => (
            <React.Fragment key={lang.code}>
              <button
                onClick={() => handleLangChange(lang.code)}
                className={`px-2 py-0.5 rounded transition-all font-bold ${activeLang === lang.code
                  ? 'bg-govt-orange text-white'
                  : 'text-white/80 hover:text-govt-orange'
                  }`}
              >{lang.label}</button>
              {i < arr.length - 1 && <span className="text-slate-600">|</span>}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* 2. MAIN HEADER */}
      <header className="bg-white py-4 px-4 md:px-20 border-b border-slate-100">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 cursor-pointer" onClick={() => setView('home')}>
          <div className="flex flex-col sm:flex-row items-center gap-4 md:gap-6 text-center sm:text-left w-full md:w-auto">
            <img
              src="/sclogo.png"
              alt="Shramjivi Logo"
              className="h-16 sm:h-20 md:h-24 w-auto object-contain drop-shadow-md shrink-0"
            />
            <div className="flex flex-col items-center sm:items-start min-w-0">
              <h1 className="text-sm sm:text-base md:text-2xl font-black text-govt-blue tracking-tighter leading-tight uppercase">
                SHRAMJIVI GOODSHED CORPORATION LIMITED
              </h1>
              <p className="text-[10px] sm:text-xs font-bold text-slate-500 tracking-[0.05em] sm:tracking-[0.1em] md:tracking-[0.2em] uppercase mt-1">
                National Goodshed Workers Welfare Initiative
              </p>
              <div className="mt-2 flex">
                <span className="bg-govt-orange/10 text-govt-orange text-[9px] md:text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded border border-govt-orange/30 shadow-sm">
                  Northeast Frontier Region
                </span>
              </div>
            </div>
          </div>
          <div className="hidden md:block shrink-0 ml-4">
            <a href="https://www.india.gov.in/" target="_blank" rel="noopener noreferrer" className="hover:opacity-90 transition-opacity">
              <img
                src="/GOV1.jpg"
                alt="National Portal of India"
                className="h-8 md:h-12 w-auto object-contain"
              />
            </a>
          </div>
        </div>
      </header>

      {/* 3. NAVIGATION BAR */}
      <nav className="bg-govt-blue text-white sticky top-0 z-40 shadow-xl border-b-4 border-govt-orange">
        <div className="container mx-auto px-4 md:px-20 flex justify-between items-center">
          {/* Desktop Navigation Links */}
          <ul className="hidden md:flex py-3 gap-6 md:gap-10 text-[11px] md:text-[13px] font-bold uppercase tracking-widest">
            {navLinks.map(link => (
              <li
                key={link.id}
                onClick={() => setView(link.id)}
                className={`whitespace-nowrap cursor-pointer hover:text-govt-orange transition-colors border-b-2 ${view === link.id ? 'border-govt-orange text-govt-orange' : 'border-transparent'}`}
              >
                {link.name}
              </li>
            ))}
          </ul>

          {/* Mobile Header / Hamburger Toggle */}
          <div className="flex md:hidden w-full justify-between items-center py-3">
            <span className="font-black text-xs uppercase tracking-widest text-govt-orange flex items-center gap-2">
              <span className="text-sm">🏛️</span> SGCL Official Portal
            </span>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-1.5 text-white hover:text-govt-orange transition-colors focus:outline-none"
              aria-label="Toggle Menu"
            >
              {isMobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 6h16M4 12h16M4 18h16"></path>
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown Links */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-[#001529]/95 backdrop-blur-md border-t border-white/5 transition-all duration-300">
            <ul className="flex flex-col px-6 py-4 gap-4 text-[10px] font-bold uppercase tracking-widest">
              {navLinks.map(link => (
                <li
                  key={link.id}
                  onClick={() => {
                    setView(link.id);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`py-2.5 cursor-pointer hover:text-govt-orange transition-colors border-b border-white/5 ${view === link.id ? 'text-govt-orange border-govt-orange/30' : 'text-slate-200'}`}
                >
                  {link.name}
                </li>
              ))}
            </ul>
          </div>
        )}
      </nav>

      {/* 4. LATEST NEWS TICKER (Marquee) */}
      <div
        onClick={() => setView('notices')}
        className="bg-slate-100 border-b border-slate-200 py-2 overflow-hidden flex items-center cursor-pointer hover:bg-orange-50/50 transition-colors group"
        title="Click to view all notices & circulars"
      >
        <div className="bg-govt-orange text-white text-[10px] font-black px-4 py-1 z-10 shadow-lg uppercase italic ml-4 md:ml-20 rounded-sm shrink-0 group-hover:scale-105 transition-transform">
          Updates
        </div>
        {/* eslint-disable-next-line jsx-a11y/no-distracting-elements */}
        <div className="overflow-hidden flex-1 ml-2">
          <div className="animate-marquee whitespace-nowrap text-xs font-bold text-govt-blue uppercase tracking-tight inline-block group-hover:text-govt-orange transition-colors">
            {tickerUpdates.length > 0 ? (
              tickerUpdates.map((u, i) => (
                <span key={u.id || i}>
                  {u.text} {i < tickerUpdates.length - 1 ? ' • ' : ''}
                </span>
              ))
            ) : (
              'Welcome to SHRAMJIVI GOODSHED CORPORATION LIMITED • Registered under MCA Govt. of India (CIN: U78300WR2026PLC293826) • Empowering Goodshed workers through digital management and corporate social security.'
            )}
          </div>
        </div>
      </div>

      {/* PAGE ROUTING LOGIC */}
      {view === 'home' ? (
        <>
          {/* 5. HERO SECTION */}
          <div className="relative min-h-[400px] md:h-[500px] bg-slate-900 flex items-center overflow-hidden border-b-8 border-govt-blue pb-16 md:pb-0">
            {['/SG (1).jpg', '/SG (2).jpg', '/SG (3).jpg'].map((bg, idx) => (
              <img
                key={idx}
                src={bg}
                alt={`Background ${idx + 1}`}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${idx === currentSlide ? 'opacity-30' : 'opacity-0'
                  }`}
              />
            ))}
            <div className="relative container mx-auto px-4 md:px-20 text-white py-12 text-left">
              <div className="max-w-2xl">
                <h2 className="text-4xl md:text-7xl font-black mb-4 leading-none uppercase italic drop-shadow-2xl font-hindi">श्रम शक्ति <br /><span className="text-govt-orange text-5xl md:text-8xl font-hindi">सर्वश्रेष्ठ शक्ति</span></h2>
                <p className="text-base md:text-xl font-medium text-slate-200 mb-8 border-l-4 border-govt-orange pl-4 italic bg-black/20 p-2 rounded">
                  Empowering Goodshed workers through digital management and corporate social security.
                </p>
                <div className="flex flex-wrap gap-4">
                  <button onClick={() => setShowLogin(true)} className="bg-govt-orange text-white px-8 py-3.5 rounded font-black uppercase text-xs shadow-[0_5px_0_rgb(194,65,12)] active:translate-y-1 transition-all">Apply for Schemes</button>
                  <button onClick={() => setView('about')} className="bg-white text-govt-blue px-8 py-3.5 rounded font-black uppercase text-xs hover:bg-slate-100 transition-colors">Know More</button>
                </div>
              </div>
            </div>
          </div>

          {/* 6. SERVICES GRID — PREMIUM GOVT PORTAL STYLE */}
          <div className="bg-slate-100 border-t-4 border-govt-blue py-12 md:py-16">
            <div className="container mx-auto px-4 md:px-20">

              {/* Section Header */}
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-5 py-2 shadow-sm mb-4">
                  <span className="w-2 h-2 rounded-full bg-[#FF9933] inline-block"></span>
                  <span className="w-2 h-2 rounded-full bg-white border border-slate-300 inline-block"></span>
                  <span className="w-2 h-2 rounded-full bg-[#138808] inline-block"></span>
                  <span className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500 ml-1">Quick Access Portal</span>
                </div>
                <h2 className="text-xl md:text-2xl font-black text-govt-blue uppercase tracking-wider">Government Schemes & Services</h2>
                <p className="text-slate-500 text-xs font-semibold mt-2 tracking-wide">Official links to central government welfare portals</p>
              </div>

              {/* Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 md:gap-6">
                {services.filter(s => s.id !== 'contact').map((s, i) => (
                  <div
                    key={i}
                    onClick={() => {
                      if (s.link) window.open(s.link, "_blank");
                      else if (s.useModal) setShowLogin(true);
                      else if (s.id) setView(s.id);
                    }}
                    className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-400 cursor-pointer flex flex-col border border-slate-200"
                  >
                    {/* Dept Header */}
                    <div
                      className="px-5 pt-6 pb-5 flex flex-col items-center gap-4 relative overflow-hidden"
                      style={{ background: s.color }}
                    >
                      {/* Watermark circle */}
                      <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-10 bg-white" />
                      <div className="absolute -left-4 -bottom-4 w-16 h-16 rounded-full opacity-10 bg-white" />

                      {/* Logo */}
                      <div className="relative z-10 h-16 flex items-center justify-center">
                        {s.iconType === 'emoji' ? (
                          <div className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center text-4xl border border-white/20 shadow-lg group-hover:scale-110 transition-transform duration-300">
                            {s.icon}
                          </div>
                        ) : (
                          <div className="bg-white rounded-xl p-2.5 shadow-lg group-hover:scale-105 transition-transform duration-300">
                            <img
                              src={s.icon}
                              alt={s.title}
                              className="h-10 w-auto object-contain max-w-[100px]"
                            />
                          </div>
                        )}
                      </div>

                      {/* Dept name */}
                      <p className="relative z-10 text-white/70 text-[9px] font-bold uppercase tracking-widest text-center leading-tight max-w-[180px]">
                        {s.dept}
                      </p>
                    </div>

                    {/* Tricolor divider */}
                    <div className="flex h-[3px]">
                      <div className="flex-1 bg-[#FF9933]" />
                      <div className="flex-1 bg-white" />
                      <div className="flex-1 bg-[#138808]" />
                    </div>

                    {/* Card Body */}
                    <div className="p-5 flex flex-col flex-1">
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <h3 className="font-black text-slate-800 text-sm md:text-base leading-tight uppercase" style={{ color: s.color }}>
                          {s.title}
                        </h3>
                        {s.link ? (
                          <span className="shrink-0 bg-green-500/10 text-green-600 text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded border border-green-500/20">
                            Govt Link
                          </span>
                        ) : (
                          <span className="shrink-0 bg-govt-blue/15 text-govt-blue text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded border border-govt-blue/20">
                            SGCL Portal
                          </span>
                        )}
                      </div>
                      <p className="text-slate-500 text-[11px] md:text-xs font-medium leading-relaxed flex-1 mb-5">
                        {s.desc}
                      </p>

                      {/* CTA Button */}
                      <button
                        className="w-full py-2.5 rounded-lg text-white text-[11px] font-black uppercase tracking-widest flex items-center justify-center gap-2 group-hover:brightness-110 transition-all"
                        style={{ background: s.color }}
                      >
                        {s.cta}
                        <span className="text-xs group-hover:translate-x-1 transition-transform duration-200">→</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </>
      ) : view === 'about' ? (
        <About />
      ) : view === 'management' ? (
        <Management />
      ) : view === 'worker-status' ? (
        <WorkerStatus />
      ) : view === 'contact' ? (
        <Contact />
      ) : view === 'services' ? (
        <ServicesPage setView={setView} setShowLogin={setShowLogin} services={services} />
      ) : view === 'notices' ? (
        <NoticesPage setView={setView} notices={notices} />
      ) : view === 'downloads' ? (
        <DownloadsPage downloads={downloads} />
      ) : view === 'career' ? (
        <Career />
      ) : view === 'gallery' ? (
        <Gallery />
      ) : view === 'no-sell-data' ? (
        <NoSellDataPage />
      ) : view === 'terms-conditions' ? (
        <TermsConditions />
      ) : view === 'privacy-policy' ? (
        <PrivacyPolicy />
      ) : view === 'disclaimer' ? (
        <DisclaimerPolicy />
      ) : view === 'cookie-policy' ? (
        <CookiePolicy />
      ) : (
        <div className="py-40 text-center container mx-auto px-4">
          <h2 className="text-3xl font-black text-govt-blue uppercase italic">Section Under Construction</h2>
          <p className="mt-4 text-slate-500 font-bold">This page for {view} is being updated according to MCA protocols.</p>
          <button onClick={() => setView('home')} className="mt-8 bg-govt-blue text-white px-6 py-2 rounded-md font-bold uppercase text-xs">Back to Home</button>
        </div>
      )}

      {/* 7. RECTANGULAR AUTHORITY LOGIN MODAL */}
      {showLogin && (
        <div className="fixed inset-0 bg-[#001529]/98 backdrop-blur-2xl z-[100] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-4xl min-h-[620px] rounded-[2.5rem] shadow-[0_50px_120px_rgba(0,0,0,0.7)] overflow-hidden animate-in fade-in zoom-in duration-500 flex flex-col md:flex-row border border-white/10">
            {/* Modal Content (Your original dual OTP logic here) */}
            <div className="p-12 w-full flex flex-col justify-center items-center">
              <h2 className="text-2xl font-black mb-4">Authority Login</h2>
              <p className="mb-8">Access restricted to authorized personnel only.</p>
              <button onClick={() => setShowLogin(false)} className="bg-govt-blue text-white px-8 py-2 rounded">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* 8. NEXT-GEN PROFESSIONAL FOOTER */}
      <footer className="mt-20 bg-[#001529] text-white pt-16 pb-6">
        <div className="container mx-auto px-4 md:px-20">

          {/* Upper Section: Grid of Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">

            {/* Col 1: Brand Identity - Fixed Logo */}
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                {/* Filters removed and height adjusted for a cleaner look */}
                <img
                  src="/sclogo.png"
                  className="h-12 md:h-14 w-auto object-contain"
                  alt="Shramjivi Footer Logo"
                />
                <div className="border-l border-white/20 pl-3">
                  <p className="text-[10px] font-black leading-tight tracking-tighter uppercase">
                    Shramjivi Goodshed<br />Corporation Ltd.
                  </p>
                </div>
              </div>

              <p className="text-slate-400 text-[11px] font-bold leading-relaxed uppercase tracking-tighter">
                A Public Limited Company incorporated under the Ministry of Corporate Affairs (MCA). <br />
                Transforming Indian goodshed operations
              </p>

              <div className="inline-block bg-white/5 px-4 py-2 rounded border border-white/10 font-mono text-[10px] text-govt-orange font-bold">
                CIN: U78300WR2026PLC293826
              </div>
            </div>

            {/* Col 2: Useful Navigation */}
            <div>
              <h4 className="text-white font-black text-sm uppercase tracking-widest mb-8 relative">
                Quick Navigation
                <span className="absolute -bottom-2 left-0 w-8 h-1 bg-govt-orange"></span>
              </h4>
              <ul className="space-y-3 text-slate-400 text-[11px] font-black uppercase tracking-widest">
                <li className="hover:text-govt-orange cursor-pointer transition-all" onClick={() => setView('home')}>Home Portal</li>
                <li className="hover:text-govt-orange cursor-pointer transition-all" onClick={() => setView('about')}>About Corporation</li>
                <li className="hover:text-govt-orange cursor-pointer transition-all" onClick={() => setView('career')}>Career Opportunities</li>
                <li className="hover:text-govt-orange cursor-pointer transition-all" onClick={() => setView('gallery')}>Media Gallery</li>
                <li className="hover:text-govt-orange cursor-pointer transition-all" onClick={() => setView('contact')}>Contact Support</li>
                <li className="hover:text-govt-orange cursor-pointer transition-all" onClick={() => setView('no-sell-data')}>We Don't Sell Data</li>
              </ul>
            </div>

            {/* Col 3: Contact Details */}
            <div>
              <h4 className="text-white font-black text-sm uppercase tracking-widest mb-8 relative">
                Contact Details
                <span className="absolute -bottom-2 left-0 w-8 h-1 bg-govt-orange"></span>
              </h4>
              <div className="space-y-5 text-slate-400 text-[11px] font-bold">
                <div className="flex items-start gap-3 group">
                  <div className="w-8 h-8 rounded bg-white/5 flex items-center justify-center text-govt-orange group-hover:bg-govt-orange group-hover:text-white transition-all italic font-black shrink-0">✉️</div>
                  <div>
                    <p className="text-[9px] font-black uppercase text-slate-500">NFR Zone Support Email</p>
                    <a href="mailto:shramjivinortheast@gmail.com" className="group-hover:text-white transition-colors block text-slate-300">
                      shramjivinortheast@gmail.com <br />
                      <span className="text-[9px] text-govt-orange font-bold">(For NFR ZONE)</span>
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3 group border-t border-white/5 pt-4">
                  <div className="w-8 h-8 rounded bg-white/5 flex items-center justify-center text-govt-orange group-hover:bg-govt-orange group-hover:text-white transition-all italic font-black shrink-0">🌐</div>
                  <div>
                    <p className="text-[9px] font-black uppercase text-slate-500">Corporate Website</p>
                    <a href="https://www.shramjivicorporation.co.in" target="_blank" rel="noopener noreferrer" className="group-hover:text-white transition-colors block text-slate-300 hover:underline">
                      www.shramjivicorporation.co.in
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Col 4: Registered & Zonal Offices */}
            <div>
              <h4 className="text-white font-black text-sm uppercase tracking-widest mb-8 relative">
                Our Offices
                <span className="absolute -bottom-2 left-0 w-8 h-1 bg-govt-orange"></span>
              </h4>
              <div className="space-y-4 text-slate-400 text-[11px] font-bold">
                <div>
                  <p className="text-[10px] font-black uppercase text-govt-orange">Registered Office</p>
                  <p className="leading-relaxed uppercase italic text-slate-300 mt-0.5">
                    D/14, Satyajit Roy Co-operative Market, <br />
                    Champasari More, Ward No. 46, <br />
                    Pradhan Nagar, Siliguri, Darjeeling, <br />
                    West Bengal – 734003
                  </p>
                </div>
                <div className="border-t border-white/5 pt-3">
                  <p className="text-[10px] font-black uppercase text-govt-orange">Zonal Office – NFR Zone</p>
                  <p className="leading-relaxed uppercase italic text-slate-300 mt-0.5">
                    15, Bhaskar Nagar School Path, <br />
                    R.G. Baruah Road, Bamunimaidan, <br />
                    Kamrup (Metro), Assam – 781021
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4 mt-4">
                <a href="https://www.mygov.in/" target="_blank" rel="noopener noreferrer" className="hover:opacity-90 transition-opacity">
                  <img
                    src="/GOV3.jpg"
                    alt="MyGov Logo"
                    className="h-9 w-auto object-contain rounded-md"
                  />
                </a>
                <a href="https://goidirectory.gov.in/" target="_blank" rel="noopener noreferrer" className="hover:opacity-90 transition-opacity">
                  <img
                    src="/GOV4.jpg"
                    alt="GoI Directory Logo"
                    className="h-9 w-auto object-contain rounded-md"
                  />
                </a>
              </div>
            </div>

          </div>

          {/* Lower Section: Compliance & Copyright */}
          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex flex-wrap justify-center md:justify-start gap-6 text-[9px] font-black text-slate-500 uppercase tracking-widest">
              <span onClick={() => setView('privacy-policy')} className="hover:text-white cursor-pointer">Privacy Policy</span>
              <span onClick={() => setView('terms-conditions')} className="hover:text-white cursor-pointer">Terms & Conditions</span>
              <span onClick={() => setView('disclaimer')} className="hover:text-white cursor-pointer">Disclaimer</span>
              <span onClick={() => setView('cookie-policy')} className="hover:text-white cursor-pointer">Cookie Policy</span>
            </div>
            <p className="text-[10px] font-black text-slate-600 uppercase tracking-[0.4em] italic text-center md:text-right">
              Official Shramjivi Corp. Website © 2026
            </p>
          </div>

        </div>
      </footer>

      {/* 9. COOKIE CONSENT BANNER */}
      {showCookieNotice && (
        <div className="fixed bottom-6 left-6 right-6 md:left-auto md:max-w-md bg-slate-900/95 backdrop-blur-md text-white rounded-3xl p-6 shadow-[0_20px_50px_rgba(0,0,0,0.5)] border border-white/10 z-[99] animate-in slide-in-from-bottom-10 duration-500">
          <div className="flex items-start gap-4">
            <span className="text-3xl">🍪</span>
            <div>
              <h4 className="text-sm font-black uppercase tracking-wider text-govt-orange">Cookie Consent Notice</h4>
              <p className="text-[11px] text-slate-300 font-semibold leading-relaxed mt-1.5">
                We use cookies and digital indicators to optimize your experience on the SGCL official portal, analyze secure traffic, and comply with Ministry standards. For details on how we safeguard information, see our promise: <span onClick={() => { setView('no-sell-data'); setShowCookieNotice(false); }} className="text-govt-orange hover:underline cursor-pointer">We Don't Sell Data</span>.
              </p>
              <div className="mt-5 flex gap-3">
                <button
                  onClick={handleAcceptCookies}
                  className="flex-1 py-2 bg-govt-orange text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:brightness-110 active:scale-95 transition-all shadow-md"
                >
                  Accept All
                </button>
                <button
                  onClick={handleDeclineCookies}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl text-[10px] font-black uppercase tracking-widest active:scale-95 transition-all border border-white/5"
                >
                  Decline
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;